import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    if (pwd !== confirm) { toast.error("Le password non coincidono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password aggiornata. Ora puoi accedere.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile aggiornare la password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="flex items-center justify-center p-8 relative">
        <Link
          to="/"
          className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-purple-700 transition-colors"
          data-testid="reset-back-home"
        >
          <ArrowLeft size={14} weight="bold" />
          Torna alla home
        </Link>
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg ht-gradient flex items-center justify-center text-white font-bold">H</div>
            <div>
              <div className="font-display text-xl font-bold text-purple-800">Housetag.it</div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-widest">Real Estate Suite</div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold">Nuova password</h1>
          <p className="text-sm text-muted-foreground mt-2">Scegli una nuova password per il tuo account Housetag.</p>

          {!token ? (
            <div className="mt-6 p-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-900">
              Link non valido o mancante. Richiedi un nuovo reset dalla pagina <Link to="/forgot-password" className="underline">Password dimenticata</Link>.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>Nuova password (min 6 caratteri)</Label>
                <div className="relative">
                  <Input type={showPwd ? "text" : "password"} required minLength={6} value={pwd}
                    onChange={e => setPwd(e.target.value)} className="pr-10" data-testid="reset-pwd" />
                  <button type="button" onClick={() => setShowPwd(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                    data-testid="toggle-reset-pwd">
                    {showPwd ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Conferma nuova password</Label>
                <Input type={showPwd ? "text" : "password"} required minLength={6} value={confirm}
                  onChange={e => setConfirm(e.target.value)} data-testid="reset-confirm" />
              </div>
              <Button type="submit" disabled={loading} className="w-full ht-gradient border-0" data-testid="reset-submit">
                {loading ? "Aggiornamento…" : "Aggiorna password"}
              </Button>
            </form>
          )}

          <div className="mt-6 text-sm text-muted-foreground">
            <Link to="/login" className="text-purple-700 hover:underline">← Torna al login</Link>
          </div>
        </div>
      </div>
      <div className="auth-image hidden md:block" />
    </div>
  );
}
